import { ApiError, codeForStatus } from './errors';
import { createInMemoryQueueStore, type OfflineQueueStore, type QueuedRequest } from './offlineQueue';
import type { ApiClientConfig, FetchLike, HttpMethod, RequestOptions } from './types';

function defaultDelay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildUrl(baseUrl: string, path: string, params?: RequestOptions['params']): string {
  const url = new URL(path.startsWith('http') ? path : `${baseUrl}${path}`);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/**
 * The transport core every typed module (`auth`, `portal`, `labs`, …) is
 * built on. Owns: Authorization header injection, JSON (de)serialization,
 * timeout, refresh-once-on-401, 5xx retry with backoff, and the offline
 * queue for idempotent writes.
 */
export interface HttpCore {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  /**
   * For idempotent progress-style writes only (see `offlineQueue.ts`). If
   * `isOnline()` is false, queues instead of throwing and resolves with
   * `{ queued: true }`. A network-level failure (not a 4xx/5xx HTTP
   * response) while "online" also falls back to queueing rather than
   * rejecting, on the theory that a network error IS evidence of
   * connectivity loss the `isOnline()` check missed.
   */
  postIdempotent(
    path: string,
    body: Record<string, unknown>,
    opts: {
      key: string;
      mergeNumericField?: string;
      sumNumericFields?: string[];
      /** Per-field ceiling applied to merged sums (e.g. the server's per-request clamp). */
      sumNumericCaps?: Record<string, number>;
      method?: 'POST' | 'PATCH' | 'PUT';
    },
  ): Promise<{ queued: boolean }>;
  /** Replays every queued idempotent write in FIFO order. Stops removing on first failure per item so a later flush can retry it. */
  flushOfflineQueue(): Promise<{ flushed: number; failed: number }>;
  queueSize(): number;
}

export function createHttpCore(config: ApiClientConfig, queueStore: OfflineQueueStore = createInMemoryQueueStore()): HttpCore {
  const {
    baseUrl,
    fetch: fetchImpl,
    getAccessToken,
    refreshToken,
    onUnauthorized,
    isOnline = () => true,
    timeoutMs = 30_000,
    maxRetries = 3,
    retryBaseDelayMs = 300,
    delay = defaultDelay,
  } = config;

  async function fetchOnce(
    url: string,
    method: HttpMethod,
    options: RequestOptions,
    token: string | null,
  ) {
    const isRawStringBody = typeof options.body === 'string';
    const headers: Record<string, string> = { ...options.headers };
    if (options.body !== undefined && !isRawStringBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token && !options.skipAuth) headers.Authorization = `Bearer ${token}`;

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = options.timeoutMs ?? timeoutMs;
    const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      return await fetchImpl(url, {
        method,
        headers,
        body:
          options.body === undefined
            ? undefined
            : isRawStringBody
              ? (options.body as string)
              : JSON.stringify(options.body),
        signal: options.signal ?? controller?.signal,
      });
    } catch (cause) {
      const timedOut = controller?.signal.aborted ?? false;
      // Preserve the underlying fetch failure's own message (browsers use
      // distinct, meaningful strings — Chrome's is literally "Failed to
      // fetch") rather than replacing it, since callers already migrated
      // from axios (e.g. `useUserContext.ts`'s offline detector) match on
      // those substrings for their offline/network-error branch.
      const causeMessage = cause instanceof Error ? cause.message : undefined;
      throw new ApiError({
        message: timedOut ? `Request timed out after ${timeout}ms` : (causeMessage ?? 'Network request failed'),
        code: timedOut ? 'timeout' : 'network',
        status: null,
        url,
        method,
        cause,
      });
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  async function fetchWithBackoff(
    url: string,
    method: HttpMethod,
    options: RequestOptions,
    token: string | null,
  ) {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      attempt += 1;
      const res = await fetchOnce(url, method, options, token);
      const shouldRetry =
        !options.skipRetry && res.status >= 500 && res.status < 600 && attempt < maxRetries;
      if (!shouldRetry) return res;
      await delay(retryBaseDelayMs * 2 ** (attempt - 1));
    }
  }

  async function parseBody<T>(res: Awaited<ReturnType<FetchLike>>): Promise<T> {
    const text = await res.text();
    if (!text) return undefined as T;
    const contentType = res.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) return text as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      return text as unknown as T;
    }
  }

  async function toApiError(
    res: Awaited<ReturnType<FetchLike>>,
    url: string,
    method: string,
  ): Promise<ApiError> {
    const body = await parseBody<unknown>(res);
    const message =
      (typeof body === 'object' && body !== null && 'message' in body
        ? String((body as { message?: unknown }).message)
        : undefined) ?? `Request failed with status ${res.status}`;
    return new ApiError({ message, code: codeForStatus(res.status), status: res.status, url, method, body });
  }

  async function requestWithAuthRetry<T>(
    url: string,
    method: HttpMethod,
    options: RequestOptions,
    alreadyRetriedAuth: boolean,
  ): Promise<T> {
    const token = options.skipAuth ? null : await getAccessToken();
    const res = await fetchWithBackoff(url, method, options, token);

    if (res.status === 401 && !options.skipAuth) {
      if (!alreadyRetriedAuth && refreshToken) {
        const refreshed = await refreshToken();
        if (refreshed) {
          return requestWithAuthRetry<T>(url, method, options, true);
        }
      }
      onUnauthorized?.();
      throw new ApiError({
        message: 'Session expired',
        code: 'unauthorized',
        status: 401,
        url,
        method,
      });
    }

    if (!res.ok) throw await toApiError(res, url, method);
    return parseBody<T>(res);
  }

  async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const method = options.method ?? 'GET';
    const url = buildUrl(baseUrl, path, options.params);
    return requestWithAuthRetry<T>(url, method, options, false);
  }

  async function postIdempotent(
    path: string,
    body: Record<string, unknown>,
    opts: {
      key: string;
      mergeNumericField?: string;
      sumNumericFields?: string[];
      /** Per-field ceiling applied to merged sums (e.g. the server's per-request clamp). */
      sumNumericCaps?: Record<string, number>;
      method?: 'POST' | 'PATCH' | 'PUT';
    },
  ): Promise<{ queued: boolean }> {
    const method = opts.method ?? 'POST';
    const enqueue = () => {
      const item: QueuedRequest = {
        path,
        method,
        body,
        key: opts.key,
        mergeNumericField: opts.mergeNumericField,
        sumNumericFields: opts.sumNumericFields,
        sumNumericCaps: opts.sumNumericCaps,
        queuedAt: Date.now(),
      };
      queueStore.upsert(item);
    };

    if (!isOnline()) {
      enqueue();
      return { queued: true };
    }

    try {
      await request<unknown>(path, { method, body });
      return { queued: false };
    } catch (err) {
      if (err instanceof ApiError && (err.code === 'network' || err.code === 'timeout')) {
        enqueue();
        return { queued: true };
      }
      throw err;
    }
  }

  async function flushOfflineQueue(): Promise<{ flushed: number; failed: number }> {
    let flushed = 0;
    let failed = 0;
    for (const item of queueStore.all()) {
      try {
        await request<unknown>(item.path, { method: item.method, body: item.body });
        queueStore.remove(item.key);
        flushed += 1;
      } catch {
        failed += 1;
      }
    }
    return { flushed, failed };
  }

  return {
    request,
    postIdempotent,
    flushOfflineQueue,
    queueSize: () => queueStore.all().length,
  };
}
